import React, { useState, useEffect } from 'react';
import { Plus, Search } from 'lucide-react';
import TagBox from './tag-box';

export default function TagsSelector({
  tags = [],
  values,
  setFieldValue,
  orderTags = [],
  isDisplay = false,
  disabled = false,
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Clean up empty strings in tagUuids when component mounts (only when not in display mode)
  useEffect(() => {
    if (!isDisplay && values?.tagUuids && values.tagUuids.includes('')) {
      const cleanedTagUuids = values.tagUuids.filter((uuid) => uuid !== '');
      setFieldValue('tagUuids', cleanedTagUuids);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter tags based on search query and already selected tags
  const filteredTags =
    tags?.filter(
      (tag) =>
        tag.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !values?.tagUuids?.includes(tag.uuid) &&
        tag.isActive
    ) || [];

  const addTag = (tag) => {
    // Make sure we're not adding to an array with empty strings
    const currentTags = values?.tagUuids?.filter((uuid) => uuid !== '') || [];
    const newTagUuids = [...currentTags, tag.uuid];
    setFieldValue('tagUuids', newTagUuids);
    setIsDropdownOpen(false);
    setSearchQuery('');
  };

  const removeTag = (tagUuid) => {
    const newTagUuids = values?.tagUuids?.filter(
      (uuid) => uuid !== tagUuid && uuid !== ''
    );
    setFieldValue('tagUuids', newTagUuids);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => {
      setIsDropdownOpen(false);
    };

    if (isDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Determine which tags to display based on available data
  const tagsToDisplay = React.useMemo(() => {
    // If in display mode, use orderTags directly
    if (isDisplay) {
      return orderTags;
    }

    // If orderTags are provided and values.tagUuids is empty or undefined, use orderTags
    if (
      orderTags?.length > 0 &&
      (!values?.tagUuids ||
        values.tagUuids.length === 0 ||
        (values.tagUuids.length === 1 && values.tagUuids[0] === ''))
    ) {
      return orderTags;
    }

    // Otherwise, filter tags based on values.tagUuids
    return tags?.filter((tag) => values?.tagUuids?.includes(tag.uuid)) || [];
  }, [isDisplay, orderTags, tags, values?.tagUuids]);

  return (
    <div className="w-full border-b border-gray-200 bg-[#f8f9fd] px-4 py-1">
      <div
        className="flex items-center flex-wrap gap-2
      "
      >
        <span className="text-sm mr-2">Tags:</span>
        <TagBox
          tags={tagsToDisplay}
          isDisplay={isDisplay || disabled}
          onRemove={disabled ? undefined : (uuid) => {
            removeTag(uuid);
          }}
        />
        {!isDisplay && !disabled && (
          <div className="relative">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsDropdownOpen(!isDropdownOpen);
              }}
              className="flex items-center text-blue-600 text-sm px-2 py-1 hover:bg-blue-50 rounded"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add tag
            </button>

            {isDropdownOpen && (
              <div className="absolute left-0 mt-1 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                <div className="p-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search tags"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {filteredTags.length > 0 ? (
                    filteredTags.map((tag) => (
                      <button
                        key={tag.uuid}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          addTag(tag);
                        }}
                        className="flex items-center w-full px-3 py-2 text-sm text-left hover:bg-gray-100"
                      >
                        {tag.icon && <span className="mr-2">{tag.icon}</span>}
                        {tag.name}
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      No matching tags
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
